import { auth } from '../config/firebase';

/**
 * Collections the scan can look in.
 *
 * `meshy_assets` was missing here and on the server, so a topic whose models
 * live there — which is where the studio's own generated and uploaded assets go
 * — scanned clean every time, and the buttons that act on a scan result stayed
 * disabled because there was never a result to act on.
 */
export type MeshyRegenerationSourceCollection =
  | 'text_to_3d_assets'
  | 'avatar_to_3d_assets'
  | 'meshy_assets';

export type MeshyRegenerationJobStatus =
  | 'dry_run'
  | 'queued'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'cancelled'
  | 'failed';

export type MeshyRegenerationItemStatus =
  | 'scan_result'
  | 'pending'
  | 'generating_preview'
  | 'refining_texture'
  | 'finalizing'
  | 'replaced'
  | 'failed'
  | 'cancelled';

export interface MeshyRegenerationScope {
  chapterId?: string;
  topicId?: string;
  sourceCollections?: MeshyRegenerationSourceCollection[];
  limit?: number;
  healthCheck?: boolean;
  /** Only assets the recovery pass could not save. */
  repairStatus?: 'source_expired';
}

export interface MeshyRegenerationSelectedItem {
  sourceCollection: MeshyRegenerationSourceCollection;
  sourceAssetId: string;
}

export interface MeshyRegenerationCounts {
  total?: number;
  scan_result?: number;
  pending?: number;
  generating_preview?: number;
  refining_texture?: number;
  finalizing?: number;
  replaced?: number;
  failed?: number;
  cancelled?: number;
  [key: string]: number | undefined;
}

export interface MeshyRegenerationItem {
  id: string;
  status: MeshyRegenerationItemStatus;
  stage?: string;
  progress?: number;
  source_collection: MeshyRegenerationSourceCollection;
  source_asset_id: string;
  old_meshy_asset_id?: string;
  new_meshy_asset_id?: string;
  prompt?: string;
  chapter_id?: string;
  topic_id?: string;
  broken_reasons?: string[];
  old_url_type?: string;
  estimated_meshy_task_count?: number;
  render_url?: string;
  storage_path?: string;
  error?: string;
  legacy_url_snapshot?: Record<string, unknown>;
  attempt_count?: number;
}

export interface MeshyRegenerationJob {
  id: string;
  status: MeshyRegenerationJobStatus;
  dry_run?: boolean;
  created_by?: string;
  scope?: MeshyRegenerationScope;
  settings?: Record<string, unknown>;
  counts?: MeshyRegenerationCounts;
  error?: string;
  items?: MeshyRegenerationItem[];
}

export interface AssetRecoveryPage {
  processed: number;
  repaired: number;
  recovered: number;
  sourceExpired: number;
  failed: number;
  skipped: number;
  nextCursor: string | null;
}

export interface CreateMeshyRegenerationJobParams {
  dryRun?: boolean;
  scope?: MeshyRegenerationScope;
  selectedItems?: MeshyRegenerationSelectedItem[];
  settings?: Record<string, unknown>;
}

const getApiBaseUrl = () => {
  if (window.VITE_ENV.VITE_API_BASE_URL) {
    return window.VITE_ENV.VITE_API_BASE_URL;
  }

  if (window.VITE_ENV?.DEV) {
    return 'http://localhost:5001/in3devoneuralai/us-central1/api';
  }

  const region = 'us-central1';
  const projectId = window.VITE_ENV.VITE_FIREBASE_PROJECT_ID || 'in3devoneuralai';
  return `https://${region}-${projectId}.cloudfunctions.net/api`;
};

class MeshyRegenerationService {
  private baseUrl = getApiBaseUrl();

  private async authHeaders(): Promise<HeadersInit> {
    const user = auth.currentUser;
    if (!user) throw new Error('Authentication required');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await user.getIdToken()}`,
    };
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers: {
        ...(await this.authHeaders()),
        ...options.headers,
      },
    });
    const body = await response.json().catch(() => ({}));
    const raw = body?.data ?? body;
    if (!response.ok) {
      throw new Error(body?.message || raw?.message || raw?.error || `Request failed: ${response.status}`);
    }
    return raw as T;
  }

  async createJob(params: CreateMeshyRegenerationJobParams): Promise<MeshyRegenerationJob> {
    return this.request<MeshyRegenerationJob>('/meshy/regeneration/jobs', {
      method: 'POST',
      body: JSON.stringify(params),
      signal: AbortSignal.timeout(120000),
    });
  }

  /** One page of the library-wide recovery pass; repeat with `nextCursor` until it is null. */
  async recoverExpiredAssets(cursor: string | null): Promise<AssetRecoveryPage> {
    return this.request<AssetRecoveryPage>('/meshy/recover-expired-assets', {
      method: 'POST',
      body: JSON.stringify({ cursor }),
      signal: AbortSignal.timeout(300000),
    });
  }

  async getJob(jobId: string): Promise<MeshyRegenerationJob> {
    return this.request<MeshyRegenerationJob>(`/meshy/regeneration/jobs/${encodeURIComponent(jobId)}`, {
      method: 'GET',
      signal: AbortSignal.timeout(30000),
    });
  }

  async cancelJob(jobId: string): Promise<MeshyRegenerationJob> {
    return this.request<MeshyRegenerationJob>(`/meshy/regeneration/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(30000),
    });
  }

  async retryFailed(jobId: string): Promise<MeshyRegenerationJob> {
    return this.request<MeshyRegenerationJob>(`/meshy/regeneration/jobs/${encodeURIComponent(jobId)}/retry-failed`, {
      method: 'POST',
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(30000),
    });
  }
}

export const meshyRegenerationService = new MeshyRegenerationService();
